import React, { useState } from 'react';
import { animated, useSpring } from 'react-spring';
import { Field, Form } from 'formik';
import { GoogleLogin } from '@react-oauth/google';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { useTheme } from 'emotion-theming';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import Button from '../../../../shared/_TBComponents/Button';
import Checkmark from '../../../../shared/_TBComponents/Checkmark';
import encode from '../../../../shared/utils/encodeToUrl';
import Link from '../../../../shared/_TBComponents/Link';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import useLogin from '../hooks/useLogin';

const DividerContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-bottom: 10px;

  .divider-line {
    border-top: 2px solid rgb(232,232,232);
    display: flex;
    margin: 20px 10px;
    width: 100%;
  }
`;

const FieldError = styled('div')`
  margin-top: 10px;
  font-size: 14px;
  color: #ff6600;
`;

const FormGroup = styled('div')`
  .easy-join {
    margin: 30px 0px 15px;
  }
  .easy-join-alt {
    margin-top: 15px;
  }

  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
`;

const PasswordFieldWrapper = styled('div')`
  position: relative;
`;

const PasswordToggleButton = styled('button')`
  align-items: center;
  background-color: transparent;
  border: none;
  color: #666;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  justify-content: center;
  padding: 8px;
  pointer-events: auto;
  position: absolute !important;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);

  &:focus {
    outline: none;
  }

  &:hover {
    color: #333;
  }
`;

const SpinCheckContainer = styled('div')`
  display: flex;
  flex: 1;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 0;
`;

const SSOFieldError = styled('div')`
  color: #ff6600;
  font-size: 14px;
  margin-bottom: 10px;
`;

const SharedLoginForm = ({
  customLoginInfo,
  errors,
  flags,
  handleNoAccountClick,
  handleSubmit,
  googleLoginWidth,
  isSSOLogin,
  touched,
  values,
}) => {
  const isPreLoginLoading = useSelector((state) => state.auth.ui.isPreLoginLoading);
  const ssoData = useSelector((state) => state.auth.data.ssoData);

  const {
    googleError,
    handleGoogleAuth,
    handlePreLogin,
    isAuthenticated,
    isLoginLoading,
    isSSOLoginLoading,
    loginError,
    ssoLoginError,
  } = useLogin();

  const [isNextButtonClicked, setIsNextButtonClicked] = useState(false);
  const [passwordButtonClicked, setPasswordButtonClicked] = useState(false);
  const [passwordFieldBlurred, setPasswordFieldBlurred] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const completeAnimation = useSpring({ opacity: isAuthenticated ? 1 : 0 });
  const formAnimation = useSpring({
    opacity: isAuthenticated || isLoginLoading || isSSOLoginLoading ? 0 : 1,
    position: 'relative',
    zIndex: 1,
  });
  const spinnerAnimation = useSpring({
    opacity: isLoginLoading || isSSOLoginLoading ? 1 : 0,
    zIndex: 0,
  });

  const theme = useTheme();

  const usernameAlias = customLoginInfo?.usernameAlias ? `${customLoginInfo.usernameAlias} (Username)` : 'Username';

  const handleCustomeUsername = () => {
    if (customLoginInfo && customLoginInfo?.usernamesEnabled) {
      return usernameAlias;
    }
    return 'Email Address / Username';
  };

  const handleGoogleResponse = async (credentialResponse) => {
    const tokenId = credentialResponse.credential;

    if (!tokenId) {
      return;
    }

    const googleValues = {
      grant_type: 'google',
      client_id: process.env.CLIENT_ID,
      client_secret: process.env.CLIENT_SECRET,
      tokenid: tokenId,
      scope: 'offline_access',
    };

    handleGoogleAuth(encode(googleValues));
  };

  const handleSSOLogin = () => {
    const baseUrl = process.env.NODE_API_URL || `https://${process.env.ENVIRONMENT === 'PRODUCTION' ? '' : 'staging.'}api.teambuildr.com/`;
    window.location.replace(`${baseUrl}login/sso/${ssoData.ssoId}/`);
  };

  const showPasswordField = () => isNextButtonClicked
    && !isPreLoginLoading && (passwordButtonClicked || Object.keys(ssoData).length === 0);

  const showSignInWithPasswordButton = () => isNextButtonClicked
    && !isPreLoginLoading && !passwordButtonClicked && Object.keys(ssoData).length > 0;

  const showSSOButtonAndDivider = () => isNextButtonClicked
    && !isPreLoginLoading && Object.keys(ssoData).length > 0 && !passwordButtonClicked;

  return (
    <>
      <animated.div style={formAnimation}>
        <Form
          name='login'
          id='login'
        >
          {isSSOLogin && ssoLoginError ? (
            <SSOFieldError className='text-center'>Unable to login with your SSO provider. Please try again or contact support if the issue persists.</SSOFieldError>
          ) : null}
          {/* Email Address / Username field */}
          <FormGroup className='form-group'>
            <Text
              styledTextProps={{ fontSize: theme.textFontSizes[0], fontWeight: '700', marginBottom: '8px !important' }}
            >
              {handleCustomeUsername()}
            </Text>
            <Field
              className='form-control'
              id='email-field'
              name='username'
              placeholder='Email or Username'
              type='text'
            />
            {errors.username && touched.username ? <FieldError className='text-center'>{errors.username}</FieldError> : null}
          </FormGroup>
          {/* Password field after sign in w/ password button is clicked */}
          {showPasswordField() ? (
            <FormGroup className='form-group'>
              <Text
                styledTextProps={{ fontSize: theme.textFontSizes[0], fontWeight: '700', marginBottom: '8px !important' }}
              >
                Password
              </Text>
              <PasswordFieldWrapper>
                <Field
                  autoComplete='off'
                  className='form-control'
                  id='password'
                  name='password'
                  onBlur={() => setPasswordFieldBlurred(true)}
                  placeholder='Password'
                  type={showPassword ? 'text' : 'password'}
                />
                <PasswordToggleButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  tabIndex={-1}
                  type='button'
                >
                  {showPassword ? 'Hide Password' : 'Show Password'}
                  <span style={{ marginLeft: '8px' }}>
                    {showPassword ? <HiEyeOff /> : <HiEye />}
                  </span>
                </PasswordToggleButton>
              </PasswordFieldWrapper>
              {errors.password && passwordFieldBlurred ? <FieldError className='text-center'>{errors.password}</FieldError> : null}
              {loginError ? <FieldError className='text-center'>Your email address or password is incorrect! Please try again.</FieldError> : null}
            </FormGroup>
          ) : null}
          {/* Google error message */}
          <FormGroup className='form-group'>
            {googleError ? <FieldError className='text-center'>This google account does not seem to be associated with a TeamBuildr account.</FieldError> : null}
          </FormGroup>
          {/* Next / Sign in with SSO / Sign in with Password buttons */}
          <FormGroup className='text-center'>
            {(!isNextButtonClicked || isPreLoginLoading) ? (
              <Button
                bgColor={theme.colors.orange}
                fontSize='14px'
                fullWidth
                isLoading={!showPasswordField() && isPreLoginLoading}
                large
                lightSpinner
                name='Next'
                noBorder
                onClick={() => {
                  setIsNextButtonClicked(true);
                  // username is the email
                  handlePreLogin(values.username);
                }}
                rounded
                styledTextProps={{
                  marginBottom: '0px !important',
                }}
                styledWrapperProps={{
                  marginBottom: '20px',
                }}
                txtColor={theme.colors.white}
                upperCase
              />
            ) : null}
            {showSSOButtonAndDivider() ? (
              <>
                <Button
                  bgColor={theme.colors.lightBlue}
                  disabled={false}
                  fontSize='14px'
                  fullWidth
                  large
                  name={`Sign in with ${ssoData.loginName}`}
                  noBorder
                  onClick={handleSSOLogin}
                  rounded
                  styledTextProps={{
                    marginBottom: '0px !important',
                  }}
                  styledWrapperProps={{
                    marginBottom: '10px',
                  }}
                  txtColor={theme.colors.white}
                  upperCase
                />
                <DividerContainer>
                  <div className='divider-line' />
                  <Text style={{ marginBottom: '0px' }} fontWeight='bold'>OR</Text>
                  <div className='divider-line' />

                </DividerContainer>
              </>
            ) : null}
            {showSignInWithPasswordButton() ? (
              <Button
                bgColor={theme.colors.orange}
                fontSize='14px'
                fullWidth
                large
                name='Sign In With Password'
                noBorder
                onClick={() => setPasswordButtonClicked(true)}
                rounded
                styledTextProps={{
                  marginBottom: '0px !important',
                }}
                styledWrapperProps={{
                  marginBottom: '20px',
                }}
                txtColor={theme.colors.white}
                upperCase
              />
            ) : null}
            {/* Sign in (submit) button */}
            {showPasswordField() ? (
              <Button
                bgColor={theme.colors.orange}
                disabled={
                  errors.password
                    || errors.username
                    || (showPasswordField() && !values.password)
                    || isLoginLoading
                }
                fontSize='14px'
                fullWidth
                isLoading={isLoginLoading}
                large
                lightSpinner
                name='Sign In'
                noBorder
                onClick={handleSubmit}
                rounded
                styledTextProps={{
                  marginBottom: '0px !important',
                }}
                styledWrapperProps={{
                  marginBottom: '20px',
                }}
                txtColor={theme.colors.white}
                upperCase
              />
            ) : null}
            {/* Google login button */}
            <GoogleLogin
              locale='en'
              onSuccess={handleGoogleResponse}
              text='signin_with'
              width={googleLoginWidth}
            />
            {/* Create account button (program/shared-workout pages) */}
            {handleNoAccountClick ? (
              <Button
                fontSize='14px'
                fullWidth
                large
                name='Don&apos;t have an account? Click here to create one'
                noBorder
                onClick={handleNoAccountClick}
                styledWrapperProps={{ margin: '25px 0px 0px' }}
                styledTextProps={{
                  marginBottom: '0px !important',
                }}
                txtColor={theme.colors.lightText[1]}
                type='button'
                upperCase
              />
            ) : null}
            {/* Easy join and forgot password links */}
            {flags.registration ? (
              <Link allCaps className='easy-join' color='lightText.1' href='/signup/easy-join'>
                Have an Easy Join Code? Click Here
              </Link>
            ) : (
              <Link allCaps className='easy-join' color='lightText.1' href='https://app.teambuildr.com/signup?ej=true'>
                Have an Easy Join Code? Click Here
              </Link>
            )}
            <Link allCaps color='lightText.2' href='/forgot'>
              Forgot Your Password?
            </Link>
          </FormGroup>
        </Form>
      </animated.div>
      <animated.div style={spinnerAnimation}>
        <SpinCheckContainer>
          <Spinner />
        </SpinCheckContainer>
      </animated.div>
      <animated.div style={completeAnimation}>
        <SpinCheckContainer>
          <Checkmark />
        </SpinCheckContainer>
      </animated.div>
    </>
  );
};

SharedLoginForm.propTypes = {
  customLoginInfo: PropTypes.shape({
    usernameAlias: PropTypes.string,
    usernamesEnabled: PropTypes.bool,
  }),
  errors: PropTypes.shape({
    username: PropTypes.string,
    password: PropTypes.string,
  }).isRequired,
  flags: PropTypes.shape({
    registration: PropTypes.bool.isRequired,
  }).isRequired,
  googleLoginWidth: PropTypes.string.isRequired,
  handleNoAccountClick: PropTypes.func,
  handleSubmit: PropTypes.func.isRequired,
  isSSOLogin: PropTypes.bool,
  touched: PropTypes.shape({
    username: PropTypes.bool,
    password: PropTypes.bool,
  }).isRequired,
  values: PropTypes.shape({
    username: PropTypes.string,
    password: PropTypes.string,
  }).isRequired,
};

SharedLoginForm.defaultProps = {
  customLoginInfo: null,
  handleNoAccountClick: null,
  isSSOLogin: false,
};

export default withLDConsumer()(SharedLoginForm);
